/** 1·2학기 AI 범교과 자동 구성 — 클라이언트 요청/후처리 (학생 정보 없음) */
import { supabase } from '../supabaseClient';
import { aiAccessErrorMessage, syncAiQuotaFromResponse } from '../services/aiQuotaService';

export type AiCrossRowInput = { id: string; subject: string; unit: string; learning: string };
export type AiCrossTopicInput = { topic: string; remaining: number };
export type AiCrossItem = { id: string; topic: string; content: string };

const ERRORS: Record<string, string> = {
  AI_NOT_CONFIGURED: 'AI 키가 설정되지 않았습니다.',
  UNAUTHORIZED: '로그인이 필요합니다. 다시 로그인해 주세요.',
  FORBIDDEN: '현재 이용권에서는 AI 범교과 자동 구성을 사용할 수 없습니다.',
  RATE_LIMITED: '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.',
  TOO_MANY_ROWS: '수업 행이 너무 많습니다.',
  TOO_MANY_TOPICS: '범교과 주제가 너무 많습니다(최대 30개).',
};

function apiUrl(): string {
  const base = (process.env.REACT_APP_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';
  return `${base}/api/ai-cross-curricular`;
}

/** 서버 결과를 한 번 더 검증: 보낸 id·주제만, 행당 1개, 주제별 남은 시수 이내 */
export function sanitizeAiCrossItems(
  raw: unknown,
  rows: AiCrossRowInput[],
  topics: AiCrossTopicInput[]
): AiCrossItem[] {
  const ids = new Set(rows.map((r) => r.id));
  const left = new Map(topics.map((t) => [t.topic, Math.max(0, Math.floor(t.remaining))]));
  const used = new Set<string>();
  const out: AiCrossItem[] = [];
  for (const it of Array.isArray(raw) ? raw : []) {
    const id = String((it as AiCrossItem)?.id ?? '');
    const topic = String((it as AiCrossItem)?.topic ?? '').trim();
    const n = left.get(topic) ?? 0;
    if (!ids.has(id) || used.has(id) || n <= 0) continue;
    used.add(id);
    left.set(topic, n - 1);
    out.push({ id, topic, content: String((it as AiCrossItem)?.content ?? '').trim().slice(0, 200) });
  }
  return out;
}

export async function requestAiCrossCurricular(
  params: { termNumber: 1 | 2; semesterInfo: string; topics: AiCrossTopicInput[]; rows: AiCrossRowInput[] },
  signal?: AbortSignal
): Promise<AiCrossItem[]> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error(ERRORS.UNAUTHORIZED);
  let res: Response;
  try {
    res = await fetch(apiUrl(), {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(params),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new Error('AI 서버에 연결하지 못했습니다.');
  }
  const json = (await res.json().catch(() => null)) as { ok?: boolean; items?: unknown; error?: string } | null;
  syncAiQuotaFromResponse(json);
  if (!json || !json.ok) {
    const code = json?.error || `HTTP_${res.status}`;
    throw new Error(aiAccessErrorMessage(code, json) ?? ERRORS[code] ?? `AI 범교과 구성을 받지 못했습니다. (${code})`);
  }
  return sanitizeAiCrossItems(json.items, params.rows, params.topics);
}
